import React, { useState, useCallback, useMemo, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { EventClickArg, EventDropArg, DatesSetArg } from '@fullcalendar/core';
import { EventResizeDoneArg } from '@fullcalendar/interaction';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { EquipmentReservation, ReservationStatus } from '@models/warehouse.model';
import { useEquipmentReservationsForRange } from '@hooks/api/warehouse/useEquipmentReservationsForRange';
import { useReservationsByTaskForRange } from '@hooks/api/warehouse/useEquipmentReservationsForRange';
import { useUpdateEquipmentReservation } from '@hooks/api/warehouse/useEquipmentAssetMutations';
import { EquipmentReservationEditModal } from '@components/organisms/EquipmentReservationEditModal';
import { toast } from 'react-toastify';
import './calendarStyles.css';
import { useTranslation } from '@hooks/useTranslation';

const CHECKOUT_COLORS = { bg: '#3B82F6', border: '#2563EB', text: '#ffffff' };

const STATUS_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  [ReservationStatus.ACTIVE]: { bg: '#7C3AED', border: '#6D28D9', text: '#ffffff' },
  [ReservationStatus.COMPLETED]: { bg: '#9CA3AF', border: '#6B7280', text: '#ffffff' },
  [ReservationStatus.CANCELLED]: { bg: '#FCA5A5', border: '#EF4444', text: '#991B1B' },
};

interface EquipmentReservationCalendarProps {
  assetId?: string;
  taskId?: string;
  height?: number | string;
  onReservationChange?: () => void;
}

export const EquipmentReservationCalendar: React.FC<EquipmentReservationCalendarProps> = ({
  assetId,
  taskId,
  height = 600,
  onReservationChange,
}) => {
  const tActionMessages = useTranslation('actionMessages');
  const calendarRef = useRef<FullCalendar>(null);
  const [rangeStart, setRangeStart] = useState<string | undefined>();
  const [rangeEnd, setRangeEnd] = useState<string | undefined>();
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedReservation, setSelectedReservation] = useState<EquipmentReservation | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const updateReservation = useUpdateEquipmentReservation();

  const MONTH_NAMES = useMemo(() => [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ], []);

  // Fetch reservations based on mode (asset or task)
  const {
    data: assetReservations = [],
    isLoading: assetLoading,
  } = useEquipmentReservationsForRange(assetId, rangeStart, rangeEnd);

  const {
    data: taskReservations = [],
    isLoading: taskLoading,
  } = useReservationsByTaskForRange(taskId, rangeStart, rangeEnd);

  const reservations = assetId ? assetReservations : taskReservations;
  const isLoading = assetId ? assetLoading : taskLoading;

  // Transform reservations into FullCalendar events (only ACTIVE)
  const events = useMemo(() => {
    return reservations.filter((r) => r.status === ReservationStatus.ACTIVE).map((r) => {
      const isCheckout = r.isCheckoutReservation === true;
      const colors = isCheckout
        ? CHECKOUT_COLORS
        : (STATUS_COLORS[r.status] || STATUS_COLORS[ReservationStatus.ACTIVE]);
      const isActive = r.status === ReservationStatus.ACTIVE;

      const title = isCheckout
        ? `[In Use] ${taskId
            ? `${r.asset?.name || 'Equipment'}`
            : `${r.task?.name || 'Unknown task'}`}`
        : taskId
          ? `${r.asset?.name || 'Equipment'} ${r.asset?.assetCode ? `(${r.asset.assetCode})` : ''}`
          : `${r.task?.name || 'Unknown task'}${r.task?.project?.name ? ` - ${r.task.project.name}` : ''}`;

      return {
        id: r.reservationId,
        title,
        start: r.startDate,
        end: r.endDate,
        backgroundColor: colors.bg,
        borderColor: colors.border,
        textColor: colors.text,
        editable: isActive && !isCheckout,
        extendedProps: { reservation: r },
      };
    });
  }, [reservations, taskId]);

  // Handle calendar date range changes
  const handleDatesSet = useCallback((arg: DatesSetArg) => {
    setRangeStart(arg.start.toISOString());
    setRangeEnd(arg.end.toISOString());
    // Track current visible month/year from the midpoint of the range
    const midDate = new Date((arg.start.getTime() + arg.end.getTime()) / 2);
    setCurrentMonth(midDate.getMonth());
    setCurrentYear(midDate.getFullYear());
  }, []);

  // Month navigation handlers
  const goToMonth = useCallback((month: number) => {
    const api = calendarRef.current?.getApi();
    if (api) {
      api.gotoDate(new Date(currentYear, month, 1));
    }
  }, [currentYear]);

  const goToPrevYear = useCallback(() => {
    const api = calendarRef.current?.getApi();
    if (api) {
      api.gotoDate(new Date(currentYear - 1, currentMonth, 1));
    }
  }, [currentYear, currentMonth]);

  const goToNextYear = useCallback(() => {
    const api = calendarRef.current?.getApi();
    if (api) {
      api.gotoDate(new Date(currentYear + 1, currentMonth, 1));
    }
  }, [currentYear, currentMonth]);

  const goToToday = useCallback(() => {
    const api = calendarRef.current?.getApi();
    if (api) {
      api.today();
    }
  }, []);

  const todayMonth = new Date().getMonth();
  const todayYear = new Date().getFullYear();
  const isCurrentMonthToday = currentMonth === todayMonth && currentYear === todayYear;

  // Handle event click — open edit modal (skip checkout reservations)
  const handleEventClick = useCallback((arg: EventClickArg) => {
    const reservation = arg.event.extendedProps.reservation as EquipmentReservation;
    if (reservation && reservation.status === ReservationStatus.ACTIVE && !reservation.isCheckoutReservation) {
      setSelectedReservation(reservation);
      setEditModalOpen(true);
    }
  }, []);

  // Handle drag-and-drop
  const handleEventDrop = useCallback(async (arg: EventDropArg) => {
    const reservation = arg.event.extendedProps.reservation as EquipmentReservation;
    if (!reservation || reservation.status !== ReservationStatus.ACTIVE) {
      arg.revert();
      return;
    }

    try {
      await updateReservation.mutateAsync({
        reservationId: reservation.reservationId,
        startDate: arg.event.start!.toISOString(),
        endDate: arg.event.end!.toISOString(),
      });
      toast.success(tActionMessages.reservationMovedSuccessfully);
      onReservationChange?.();
    } catch {
      arg.revert();
      toast.error(tActionMessages.failedToMoveReservation);
    }
  }, [
    updateReservation,
    onReservationChange,
    tActionMessages.reservationMovedSuccessfully,
    tActionMessages.failedToMoveReservation,
  ]);

  // Handle resize from edges
  const handleEventResize = useCallback(async (arg: EventResizeDoneArg) => {
    const reservation = arg.event.extendedProps.reservation as EquipmentReservation;
    if (!reservation || reservation.status !== ReservationStatus.ACTIVE) {
      arg.revert();
      return;
    }

    try {
      await updateReservation.mutateAsync({
        reservationId: reservation.reservationId,
        startDate: arg.event.start!.toISOString(),
        endDate: arg.event.end!.toISOString(),
      });
      toast.success(tActionMessages.reservationUpdatedSuccessfully);
      onReservationChange?.();
    } catch {
      arg.revert();
      toast.error(tActionMessages.failedToResizeReservation);
    }
  }, [
    updateReservation,
    onReservationChange,
    tActionMessages.reservationUpdatedSuccessfully,
    tActionMessages.failedToResizeReservation,
  ]);

  const handleEditSuccess = useCallback(() => {
    setEditModalOpen(false);
    setSelectedReservation(null);
    onReservationChange?.();
  }, [onReservationChange]);

  return (
    <div className="equipment-reservation-calendar">
      {/* Custom Month Navigation */}
      <div className="month-navigation">
        <div className="month-nav-left">
          <button type="button"
            onClick={goToPrevYear}
            className="month-nav-arrow"
            title="Previous year"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="month-nav-year">{currentYear}</span>
          <button type="button"
            onClick={goToNextYear}
            className="month-nav-arrow"
            title="Next year"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        <div className="month-nav-months">
          {MONTH_NAMES.map((name, idx) => {
            const isActive = idx === currentMonth;
            const isToday = idx === todayMonth && currentYear === todayYear;
            return (
              <button type="button"
                key={name}
                onClick={() => goToMonth(idx)}
                className={`month-nav-btn ${isActive ? 'month-nav-btn-active' : ''} ${!isActive && isToday ? 'month-nav-btn-today' : ''}`}
              >
                {name}
              </button>
            );
          })}
        </div>

        <button type="button"
          onClick={goToToday}
          disabled={isCurrentMonthToday}
          className="month-nav-today-btn"
        >
          Today
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600" />
          <span className="ml-2 text-sm text-gray-500">Loading reservations...</span>
        </div>
      )}

      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        headerToolbar={{
          left: '',
          center: '',
          right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
        }}
        buttonText={{
          month: 'month',
          week: 'week',
          day: 'day',
          list: 'list',
        }}
        events={events}
        height={height}
        editable
        eventDurationEditable
        eventStartEditable
        datesSet={handleDatesSet}
        eventClick={handleEventClick}
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        nowIndicator
        dayMaxEvents={3}
        eventTimeFormat={{
          hour: 'numeric',
          minute: '2-digit',
          meridiem: 'short',
        }}
        slotMinTime="06:00:00"
        slotMaxTime="22:00:00"
        allDaySlot={false}
      />

      {/* Legend */}
      <div className="flex items-center gap-4 mt-3 px-1">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#7C3AED' }} />
          <span className="text-xs text-gray-500">Reserved</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#3B82F6' }} />
          <span className="text-xs text-gray-500">Checked Out</span>
        </div>
        <span className="text-xs text-gray-400 ml-auto">
          Drag to move · Drag edges to resize
        </span>
      </div>

      {/* Edit Modal */}
      <EquipmentReservationEditModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedReservation(null);
        }}
        reservation={selectedReservation}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};

export default EquipmentReservationCalendar;
